import type { ReactNode } from "react";

import {
  APPLE_EULA,
  APPLE_REPORT_PROBLEM,
  COMPANY,
  COMPANY_ADDRESS,
  COMPANY_NO,
  MIN_AGE,
  PROCESSORS,
  RETENTION,
  SUBSCRIPTION_PLANS,
  SUPPORT,
  TRIAL_DAYS,
  UPDATED,
} from "../lib/legal";
// The registry, never the barrel: the barrel imports all nine translations, and
// this component renders in the browser (see legalDocs/registry.ts).
import {
  type ContentLang,
  LEGAL_LANG_ENDONYM,
  LEGAL_LANGS,
} from "../lib/legalDocs/registry";
import type {
  Block,
  LegalBundle,
  LegalDoc,
  ListItem,
} from "../lib/legalDocs/types";

/**
 * Renders one of the two legal documents in one of the nine languages.
 *
 * The documents are data (`lib/legalDocs`), not JSX, and every fact inside them
 * is a `{token}` resolved here — company details, the support address, and every
 * retention number. That is what makes nine translations safe: a translator can
 * only get the prose wrong, never the numbers, and there is exactly one place
 * where "180 days" is written down.
 */

/** Values that render as plain text. */
function textTokens(): Record<string, string> {
  return {
    company: COMPANY,
    companyNo: COMPANY_NO,
    address: COMPANY_ADDRESS,
    updated: UPDATED,
    minAge: String(MIN_AGE),
    trialDays: String(TRIAL_DAYS),
    prayerDays: String(RETENTION.prayerDays),
    chatDays: String(RETENTION.chatDays),
    notificationDays: String(RETENTION.notificationDays),
    ghostDays: String(RETENTION.ghostDays),
    strikeWindowDays: String(RETENTION.strikeWindowDays),
    firstBanDays: String(RETENTION.firstBanDays),
    maxBanDays: String(RETENTION.maxBanDays),
    analyticsMonths: String(RETENTION.analyticsMonths),
    diagnosticsMonths: String(RETENTION.diagnosticsMonths),
  };
}

/**
 * Link tokens. Their VISIBLE text is deliberately either a URL or the target
 * document's own translated title — never an English noun phrase that each
 * locale would then have to re-translate inside a sentence it does not control.
 */
function linkTokens(
  bundle: LegalBundle,
  lang: ContentLang,
): Record<string, ReactNode> {
  const q = `?lang=${lang}`;
  return {
    support: (
      <a key="support" href={`mailto:${SUPPORT}`}>
        {SUPPORT}
      </a>
    ),
    privacyLink: (
      <a key="privacy" href={`/privacy${q}`}>
        {bundle.privacy.title}
      </a>
    ),
    termsLink: (
      <a key="terms" href={`/terms${q}`}>
        {bundle.terms.title}
      </a>
    ),
    deleteLink: (
      <a key="delete" href="/delete">
        bibleshield.app/delete
      </a>
    ),
    appleEula: (
      <a key="eula" href={APPLE_EULA} target="_blank" rel="noreferrer">
        standard EULA
      </a>
    ),
    reportProblem: (
      <a
        key="report"
        href={APPLE_REPORT_PROBLEM}
        target="_blank"
        rel="noreferrer"
      >
        reportaproblem.apple.com
      </a>
    ),
  };
}

/** `{token}` or `**bold**`, in one pass. */
const MARKUP = /(\{[a-zA-Z]+\}|\*\*[^*]+\*\*)/g;

function renderText(
  text: string,
  bundle: LegalBundle,
  lang: ContentLang,
): ReactNode[] {
  const values = textTokens();
  const links = linkTokens(bundle, lang);
  // Index keys throughout this file: the documents are static data, the block
  // and part order never changes at runtime, and there is no state below these
  // nodes to preserve. Content-derived keys would collide instead — the same
  // sentence fragment appears in several sections.
  return text.split(MARKUP).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("{") && part.endsWith("}")) {
      const name = part.slice(1, -1);
      const link = links[name];
      // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
      if (link) return <span key={i}>{link}</span>;
      const value = values[name];
      // An unresolved token renders as itself rather than as an empty string —
      // a visible `{typo}` on the page is found in one look; a silent gap is
      // found by nobody. `legalDocs.test.ts` fails on it first regardless.
      // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
      return <span key={i}>{value ?? part}</span>;
    }
    // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
    return <span key={i}>{part}</span>;
  });
}

function Bullet({
  item,
  bundle,
  lang,
}: {
  item: ListItem;
  bundle: LegalBundle;
  lang: ContentLang;
}) {
  return (
    <li>
      {/* An em dash rather than a full stop after the label: the text that
          follows continues the sentence and is lower-case in every language, so
          "Setup answers. if you answer…" reads as a typo in all nine at once. */}
      {item.label ? <strong>{item.label}</strong> : null}
      {item.label ? " — " : null}
      {renderText(item.text, bundle, lang)}
    </li>
  );
}

function ProcessorTable({ bundle }: { bundle: LegalBundle }) {
  return (
    <div className="overflow-x-auto">
      <table>
        <thead>
          <tr>
            <th>{bundle.ui.processorService}</th>
            <th>{bundle.ui.processorPurpose}</th>
            <th>{bundle.ui.processorPolicy}</th>
          </tr>
        </thead>
        <tbody>
          {PROCESSORS.map((p) => (
            <tr key={p.name}>
              <td>{p.name}</td>
              <td>{p.purpose}</td>
              <td>
                <a href={p.href} target="_blank" rel="noreferrer">
                  {bundle.ui.policyLinkLabel}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Plans({ bundle }: { bundle: LegalBundle }) {
  const trial = bundle.ui.planTrial.replace("{trialDays}", String(TRIAL_DAYS));
  return (
    <ul>
      {SUBSCRIPTION_PLANS.map((p) => {
        // The product NAMES are what App Store Connect sells and stay as sold;
        // only the surrounding words are translated. Matching on the duration
        // rather than on the array index keeps a third plan from silently
        // inheriting the wrong sentence.
        const period = /year/.test(p.duration)
          ? bundle.ui.planYearly
          : bundle.ui.planWeekly;
        return (
          <li key={p.name}>
            <strong>{p.name}</strong> — {period}
            {p.trial ? `, ${trial}` : ""}.
          </li>
        );
      })}
    </ul>
  );
}

function Blocks({
  blocks,
  bundle,
  lang,
}: {
  blocks: Block[];
  bundle: LegalBundle;
  lang: ContentLang;
}) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "p":
            // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
            return <p key={i}>{renderText(block.text, bundle, lang)}</p>;
          case "h3":
            // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
            return <h3 key={i}>{block.text}</h3>;
          case "ul":
          case "safeguards":
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
              <ul key={i}>
                {block.items.map((item) => (
                  <Bullet
                    key={item.label ?? item.text}
                    item={item}
                    bundle={bundle}
                    lang={lang}
                  />
                ))}
              </ul>
            );
          case "processors":
            // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
            return <ProcessorTable key={i} bundle={bundle} />;
          case "plans":
            // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
            return <Plans key={i} bundle={bundle} />;
          default:
            return null;
        }
      })}
    </>
  );
}

/** The nine-way switcher. Plain anchors: this is a document, not an app screen. */
export function LanguagePicker({
  path,
  current,
  label,
}: {
  path: "/privacy" | "/terms";
  current: ContentLang;
  label: string;
}) {
  return (
    <nav
      aria-label={label}
      className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-sm"
    >
      <span className="text-ink2">{label}:</span>
      {LEGAL_LANGS.map((code) => (
        <a
          key={code}
          href={`${path}?lang=${code}`}
          hrefLang={code}
          aria-current={code === current ? "page" : undefined}
          className={
            code === current
              ? "font-semibold text-ink"
              : "text-ink2 underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          }
        >
          {LEGAL_LANG_ENDONYM[code]}
        </a>
      ))}
    </nav>
  );
}

export function LegalDocument({
  doc,
  bundle,
  lang,
}: {
  doc: LegalDoc;
  bundle: LegalBundle;
  lang: ContentLang;
}) {
  return (
    <div className="prose prose-brand mt-6 max-w-none">
      <p className="text-sm text-ink2">
        {renderText(bundle.ui.dateLine, bundle, lang)}
      </p>
      <Blocks blocks={doc.intro} bundle={bundle} lang={lang} />
      {doc.sections.map((section) => (
        <section key={section.id}>
          <h2 id={section.id}>{section.heading}</h2>
          <Blocks blocks={section.blocks} bundle={bundle} lang={lang} />
        </section>
      ))}
    </div>
  );
}
